import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  ApprovalListFilter, ApprovalOption, ApprovalRequestDetail, ApprovalRequestRow, ApprovalSubjectType, ApprovalWorkflow,
  ApprovalWorkflowRequest, ApproverOptions, SubjectApproval,
} from '../interfaces/approval.interface';

export type ApprovalTab = 'mine' | 'submitted' | 'decided' | 'all';

const clean = (f?: ApprovalListFilter) => {
  const out: Record<string, string> = {};
  if (f?.status) out.status = f.status;
  if (f?.subjectType) out.subjectType = f.subjectType;
  if (f?.search?.trim()) out.search = f.search.trim();
  if (f?.from) out.from = f.from;
  if (f?.to) out.to = f.to;
  return out;
};

export const ApprovalService = {
  list: async (tab: ApprovalTab, institutionId: string, filter?: ApprovalListFilter): Promise<ApprovalRequestRow[]> => {
    const url = tab === 'mine' ? ENDPOINTS.APPROVALS.MINE(institutionId)
      : tab === 'submitted' ? ENDPOINTS.APPROVALS.SUBMITTED(institutionId)
      : tab === 'decided' ? ENDPOINTS.APPROVALS.DECIDED(institutionId)
      : ENDPOINTS.APPROVALS.ALL(institutionId);
    return (await axios.get(url, { params: clean(filter) })).data;
  },
  get: async (requestId: string): Promise<ApprovalRequestDetail> => (await axios.get(ENDPOINTS.APPROVALS.ONE(requestId))).data,
  forSubject: async (subjectType: string, subjectId: string): Promise<SubjectApproval> =>
    (await axios.get(ENDPOINTS.APPROVALS.FOR_SUBJECT(subjectType, subjectId))).data,
  approve: async (requestId: string, comment?: string, expectedSeq?: number | null): Promise<ApprovalRequestDetail> =>
    (await axios.post(ENDPOINTS.APPROVALS.APPROVE(requestId), { comment: comment?.trim() || null, expectedSeq: expectedSeq ?? null })).data,
  reject: async (requestId: string, comment: string, expectedSeq?: number | null): Promise<ApprovalRequestDetail> =>
    (await axios.post(ENDPOINTS.APPROVALS.REJECT(requestId), { comment: comment.trim(), expectedSeq: expectedSeq ?? null })).data,
  cancel: async (requestId: string): Promise<ApprovalRequestDetail> => (await axios.post(ENDPOINTS.APPROVALS.CANCEL(requestId))).data,
  /** FW-3B: an administrator cancels a stuck request; the document goes back to a state its maker can fix. */
  adminCancel: async (requestId: string): Promise<ApprovalRequestDetail> => (await axios.post(ENDPOINTS.APPROVALS.ADMIN_CANCEL(requestId))).data,
};

export const ApprovalWorkflowService = {
  subjectTypes: async (): Promise<ApprovalSubjectType[]> => (await axios.get(ENDPOINTS.APPROVAL_WORKFLOWS.SUBJECT_TYPES)).data,
  list: async (institutionId: string, subjectType?: string): Promise<ApprovalWorkflow[]> =>
    (await axios.get(ENDPOINTS.APPROVAL_WORKFLOWS.LIST(institutionId), { params: subjectType ? { subjectType } : undefined })).data,
  options: async (institutionId: string): Promise<ApproverOptions> =>
    (await axios.get(ENDPOINTS.APPROVAL_WORKFLOWS.OPTIONS(institutionId))).data,
  /** Server-side search for the picker of a REFERENCE condition (customers ...), or the one option named by {@code id}. */
  referenceOptions: async (institutionId: string, subjectType: string, field: string, search?: string, id?: string): Promise<ApprovalOption[]> =>
    (await axios.get(ENDPOINTS.APPROVAL_WORKFLOWS.REFERENCE_OPTIONS(institutionId), { params: { subjectType, field, search: search || undefined, id: id || undefined } })).data,
  create: async (institutionId: string, dto: ApprovalWorkflowRequest): Promise<ApprovalWorkflow> =>
    (await axios.post(ENDPOINTS.APPROVAL_WORKFLOWS.LIST(institutionId), dto)).data,
  update: async (institutionId: string, ruleId: string, dto: ApprovalWorkflowRequest): Promise<ApprovalWorkflow> =>
    (await axios.put(ENDPOINTS.APPROVAL_WORKFLOWS.ONE(institutionId, ruleId), dto)).data,
  setActive: async (institutionId: string, ruleId: string, active: boolean): Promise<ApprovalWorkflow> =>
    (await axios.post(active ? ENDPOINTS.APPROVAL_WORKFLOWS.ACTIVATE(institutionId, ruleId) : ENDPOINTS.APPROVAL_WORKFLOWS.DEACTIVATE(institutionId, ruleId))).data,
  reorder: async (institutionId: string, subjectType: string, ruleIds: string[]): Promise<ApprovalWorkflow[]> =>
    (await axios.put(ENDPOINTS.APPROVAL_WORKFLOWS.ORDER(institutionId), { subjectType, ruleIds })).data,
  remove: async (institutionId: string, ruleId: string): Promise<void> => {
    await axios.delete(ENDPOINTS.APPROVAL_WORKFLOWS.ONE(institutionId, ruleId));
  },
};
